import { useEffect, useMemo, useState } from "react";
import { Download, FileText, FileSpreadsheet, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR, ORDER_STATUS_LABELS } from "@/lib/api";

const PERIODS = [["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["annually", "Annual"]];
const REPORTS = [
  ["transactions", "Transactions"],
  ["product", "By product"],
  ["category", "By category"],
  ["customer", "By customer"],
  ["school", "By school"],
  ["channel", "By channel"],
  ["performance", "Performance"],
];

const iso = (d) => d.toISOString().slice(0, 10);

function rangeFor(period, anchor) {
  const a = new Date(anchor + "T00:00:00");
  if (period === "daily") {
    const e = new Date(a); e.setDate(e.getDate() + 1);
    return { start: iso(a), end: iso(e), label: a.toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) };
  }
  if (period === "weekly") {
    const s = new Date(a); s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    const e = new Date(s); e.setDate(e.getDate() + 7);
    const endShown = new Date(e); endShown.setDate(endShown.getDate() - 1);
    return { start: iso(s), end: iso(e), label: `${s.toLocaleDateString("en-ZA", { day: "numeric", month: "short" })} – ${endShown.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" })}` };
  }
  if (period === "monthly") {
    const s = new Date(a.getFullYear(), a.getMonth(), 1);
    const e = new Date(a.getFullYear(), a.getMonth() + 1, 1);
    return { start: iso(s), end: iso(e), label: a.toLocaleDateString("en-ZA", { month: "long", year: "numeric" }) };
  }
  const s = new Date(a.getFullYear(), 0, 1);
  const e = new Date(a.getFullYear() + 1, 0, 1);
  return { start: iso(s), end: iso(e), label: `${a.getFullYear()}` };
}

function shiftAnchor(period, anchor, dir) {
  const a = new Date(anchor + "T00:00:00");
  if (period === "daily") a.setDate(a.getDate() + dir);
  else if (period === "weekly") a.setDate(a.getDate() + 7 * dir);
  else if (period === "monthly") a.setMonth(a.getMonth() + dir);
  else a.setFullYear(a.getFullYear() + dir);
  return iso(a);
}

async function downloadFile(url, params, filename) {
  const r = await api.get(url, { params, responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Delta({ d, money = true }) {
  if (!d || d.pct === null || d.pct === undefined)
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400"><Minus size={11} /> no prior data</span>;
  const up = d.change >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {up ? "+" : ""}{d.pct}% vs previous ({money ? formatR(d.previous) : d.previous})
    </span>
  );
}

function Card({ label, value, delta, money = true, testid, accent }) {
  return (
    <div className={`bg-white rounded-2xl border p-5 ${accent ? "border-gold/60" : "border-slate-200/80"}`} data-testid={testid}>
      <p className="font-display text-2xl font-extrabold text-navy">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-1">{label}</p>
      {delta && <div className="mt-1.5"><Delta d={delta} money={money} /></div>}
    </div>
  );
}

export default function AdminSales() {
  const today = iso(new Date());
  const [view, setView] = useState("dashboard");
  const [period, setPeriod] = useState("daily");
  const [anchor, setAnchor] = useState(today);
  const [report, setReport] = useState("transactions");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [perf, setPerf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState("");

  const range = useMemo(() => rangeFor(period, anchor), [period, anchor]);
  const params = useMemo(() => ({ period: "custom", start: range.start, end: range.end }), [range]);

  useEffect(() => {
    setBusy(true);
    setSummary(null); setOrders(null); setBreakdown(null); setPerf(null);
    const jobs = [api.get("/admin/reports/summary", { params }).then((r) => setSummary(r.data))];
    if (view === "reports") {
      if (report === "transactions") jobs.push(api.get("/admin/reports/orders", { params }).then((r) => setOrders(r.data.orders)));
      else if (report === "performance") jobs.push(api.get("/admin/reports/performance", { params }).then((r) => setPerf(r.data)));
      else jobs.push(api.get("/admin/reports/breakdown", { params: { ...params, by: report } }).then((r) => setBreakdown(r.data.rows)));
    }
    Promise.all(jobs).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, report, params]);

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const by = ["product", "category", "customer", "school", "channel"].includes(report) ? report : "product";
      await downloadFile("/admin/reports/export",
        { format, ...params, by },
        `btmedu-sales-${range.start}_to_${range.end}.${format}`);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting("");
    }
  };

  const cur = summary?.current;

  return (
    <div data-testid="admin-sales">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h1 className="font-display text-2xl font-bold tracking-tight text-navy">Sales</h1>
        <div className="flex gap-2">
          <button onClick={() => setView("dashboard")} className={`text-xs font-bold px-4 py-2 rounded-full ${view === "dashboard" ? "bg-navy text-white" : "bg-white border border-slate-200"}`} data-testid="sales-view-dashboard">
            Dashboard
          </button>
          <button onClick={() => setView("reports")} className={`text-xs font-bold px-4 py-2 rounded-full ${view === "reports" ? "bg-navy text-white" : "bg-white border border-slate-200"}`} data-testid="sales-view-reports">
            Reports & exports
          </button>
        </div>
      </div>

      {/* Period selector — shared by both views */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="sales-period-bar">
        <div className="flex gap-1.5 flex-wrap">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => { setPeriod(k); setAnchor(today); }} className={`text-xs font-bold px-3.5 py-1.5 rounded-full ${period === k ? "bg-gold text-white" : "bg-slate-50 border border-slate-200 text-navy"}`} data-testid={`sales-period-${k}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => setAnchor(shiftAnchor(period, anchor, -1))} className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-navy hover:border-gold" data-testid="sales-prev" aria-label="Previous period">
            <ChevronLeft size={14} />
          </button>
          <span className="text-sm font-bold text-navy min-w-[150px] text-center" data-testid="sales-range-label">{range.label}</span>
          <button onClick={() => setAnchor(shiftAnchor(period, anchor, 1))} className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-navy hover:border-gold" data-testid="sales-next" aria-label="Next period">
            <ChevronRight size={14} />
          </button>
          {anchor !== today && (
            <button onClick={() => setAnchor(today)} className="text-[10px] font-bold text-brand ml-1" data-testid="sales-today">Today</button>
          )}
        </div>
      </div>

      {busy && !summary && <div className="h-40 bg-white rounded-2xl border border-slate-200/80 animate-pulse" />}

      {/* ------------------------------ DASHBOARD ------------------------------ */}
      {view === "dashboard" && summary && cur && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Card label="Total sales value" value={formatR(cur.gross_value)} delta={summary.deltas.gross_value} testid="dash-gross" accent />
            <Card label="Orders" value={cur.orders} delta={summary.deltas.orders} money={false} testid="dash-orders" />
            <Card label="Money received" value={formatR(cur.received)} delta={summary.deltas.received} testid="dash-received" />
            <Card label="Awaiting payment" value={formatR(cur.awaiting_amount)} delta={summary.deltas.awaiting_amount} testid="dash-awaiting" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card label="Avg order value" value={formatR(cur.aov)} delta={summary.deltas.aov} testid="dash-aov" />
            <Card label="Discounts given" value={formatR(cur.discounts)} delta={summary.deltas.discounts} testid="dash-discounts" />
            <Card label="Refunds" value={formatR(cur.refund_amount)} delta={summary.deltas.refund_amount} testid="dash-refunds" />
            <Card label="Net sales" value={formatR(cur.net_sales)} delta={summary.deltas.net_sales} testid="dash-net" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="dash-order-states">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Order states</p>
              <div className="space-y-2">
                {[["Completed", cur.completed_orders, "text-emerald-600", "completed"],
                  ["Pending payment", cur.pending_orders, "text-gold-hover", "pending"],
                  ["Cancelled", cur.cancelled_orders, "text-slate-500", "cancelled"],
                  ["Refunded", cur.refunded_orders, "text-rose-500", "refunded"]].map(([l, v, c, k]) => (
                  <div key={k} className="flex justify-between items-center" data-testid={`dash-state-${k}`}>
                    <span className="text-sm text-slate-600">{l}</span>
                    <span className={`font-mono font-extrabold ${c}`}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="dash-deliveries">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Deliveries</p>
              <div className="space-y-2">
                {[["Completed deliveries", cur.deliveries_completed, "text-emerald-600", "completed"],
                  ["Pending deliveries", cur.deliveries_pending, "text-gold-hover", "pending"],
                  ["Failed deliveries", cur.deliveries_failed, "text-rose-500", "failed"]].map(([l, v, c, k]) => (
                  <div key={k} className="flex justify-between items-center" data-testid={`dash-delivery-${k}`}>
                    <span className="text-sm text-slate-600">{l}</span>
                    <span className={`font-mono font-extrabold ${c}`}>{v}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-3">Pending = paid orders being prepared or in transit.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="dash-best-sellers">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Best-selling products</p>
              {summary.top_products.length === 0 && <p className="text-xs text-slate-400">No sales in this period.</p>}
              <div className="space-y-2">
                {summary.top_products.map((p, i) => (
                  <div key={p.sku || p.name} className="flex items-center gap-2" data-testid={`dash-top-${i}`}>
                    <span className="text-[10px] font-extrabold text-gold w-4">{i + 1}</span>
                    <span className="text-xs text-slate-600 flex-1 truncate" title={p.name}>{p.name}</span>
                    <span className="text-[10px] font-bold text-slate-400">{p.units} sold</span>
                    <span className="text-xs font-mono font-bold text-navy">{formatR(p.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="dash-fulfilment">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Live fulfilment buckets (all open orders)</p>
            <div className="flex gap-2 flex-wrap">
              {summary.fulfilment.map((b) => (
                <span key={b.bucket} className="text-[11px] font-semibold bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-slate-600" data-testid={`dash-bucket-${b.bucket}`}>
                  {b.label}: <span className="font-extrabold text-navy">{b.count}</span> · {formatR(b.value)}
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ------------------------------ REPORTS ------------------------------ */}
      {view === "reports" && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
            <div className="flex gap-1.5 flex-wrap">
              {REPORTS.map(([k, l]) => (
                <button key={k} onClick={() => setReport(k)} className={`text-xs font-bold px-3.5 py-1.5 rounded-full ${report === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy"}`} data-testid={`report-tab-${k}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => exportReport("xlsx")} disabled={!!exporting} className="h-9 px-4 rounded-full bg-white border border-slate-200 text-xs font-bold text-navy flex items-center gap-1.5 hover:border-gold disabled:opacity-40 transition-colors" data-testid="report-export-xlsx">
                <FileSpreadsheet size={13} /> {exporting === "xlsx" ? "Exporting…" : "Excel"}
              </button>
              <button onClick={() => exportReport("csv")} disabled={!!exporting} className="h-9 px-4 rounded-full bg-white border border-slate-200 text-xs font-bold text-navy flex items-center gap-1.5 hover:border-gold disabled:opacity-40 transition-colors" data-testid="report-export-csv">
                <Download size={13} /> {exporting === "csv" ? "Exporting…" : "CSV"}
              </button>
              <button onClick={() => exportReport("pdf")} disabled={!!exporting} className="h-9 px-4 rounded-full bg-navy text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40 transition-colors" data-testid="report-export-pdf">
                <FileText size={13} /> {exporting === "pdf" ? "Exporting…" : "PDF"}
              </button>
            </div>
          </div>

          {/* Transactions table */}
          {report === "transactions" && orders && (
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]" data-testid="sales-table">
                  <thead>
                    <tr className="bg-cream text-left text-[10px] uppercase tracking-[0.15em] text-slate-500">
                      <th className="px-4 py-3">Order</th><th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Items</th>
                      <th className="px-4 py-3">Total</th><th className="px-4 py-3">Payment</th>
                      <th className="px-4 py-3">Status</th><th className="px-4 py-3">Channel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-slate-100" data-testid={`sales-row-${o.order_number}`}>
                        <td className="px-4 py-3 font-mono font-bold text-navy">{o.order_number}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-ZA")}</td>
                        <td className="px-4 py-3">{o.customer.name}</td>
                        <td className="px-4 py-3">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                        <td className="px-4 py-3 font-mono font-semibold">{formatR(o.total)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-gold/15 text-gold-hover"}`}>
                            {o.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">{ORDER_STATUS_LABELS[o.status] || o.status}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{o.channel === "app" ? "App" : "Website"}</td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">No transactions in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Breakdown tables */}
          {["product", "category", "customer", "school", "channel"].includes(report) && breakdown && (
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="breakdown-table">
                  <thead>
                    {report === "product" ? (
                      <tr className="bg-cream text-left text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        <th className="px-4 py-3">Product</th><th className="px-4 py-3">SKU</th>
                        <th className="px-4 py-3">Orders</th><th className="px-4 py-3">Units</th>
                        <th className="px-4 py-3 text-right">Revenue</th>
                      </tr>
                    ) : (
                      <tr className="bg-cream text-left text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        <th className="px-4 py-3">{report}</th><th className="px-4 py-3">Orders</th>
                        <th className="px-4 py-3">Units</th><th className="px-4 py-3 text-right">Gross</th>
                        <th className="px-4 py-3 text-right">Received</th><th className="px-4 py-3 text-right">Refunds</th>
                        <th className="px-4 py-3 text-right">Net</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {breakdown.map((r, i) => (
                      <tr key={r.key} className="border-t border-slate-100" data-testid={`breakdown-row-${i}`}>
                        <td className="px-4 py-3 font-semibold text-navy">{r.label}{r.email ? <span className="block text-[10px] font-normal text-slate-400">{r.email}</span> : null}</td>
                        {report === "product" && <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.sku || "—"}</td>}
                        <td className="px-4 py-3">{r.orders}</td>
                        <td className="px-4 py-3">{r.units}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">{formatR(r.gross)}</td>
                        {report !== "product" && (
                          <>
                            <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatR(r.received)}</td>
                            <td className="px-4 py-3 text-right font-mono text-rose-500">{formatR(r.refunds)}</td>
                            <td className="px-4 py-3 text-right font-mono font-bold">{formatR(r.net)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                    {breakdown.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">No sales in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Performance */}
          {report === "performance" && perf && (
            <div className="space-y-4" data-testid="performance-panel">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {[["Completion rate", perf.rates.completion, "completion"],
                  ["Cancellation rate", perf.rates.cancellation, "cancellation"],
                  ["Refund rate", perf.rates.refund, "refund"],
                  ["Failed-delivery rate", perf.rates.failed_delivery, "failed-delivery"],
                  ["Missing-item claim rate", perf.rates.missing_item_claims, "missing-items"]].map(([l, v, k]) => (
                  <div key={k} className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid={`perf-rate-${k}`}>
                    <p className="font-display text-2xl font-extrabold text-navy">{v}%</p>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-1">{l}</p>
                  </div>
                ))}
              </div>
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="perf-reconciliation">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Payment & refund reconciliation</p>
                  <div className="space-y-2 text-sm">
                    {[["Gross sales", perf.reconciliation.gross_sales],
                      ["Discounts given", perf.reconciliation.discounts],
                      ["Refunds paid out", perf.reconciliation.refunds],
                      ["Net sales", perf.reconciliation.net_sales],
                      ["Money received", perf.reconciliation.received],
                      ["Awaiting payment", perf.reconciliation.awaiting_payment]].map(([l, v]) => (
                      <div key={l} className="flex justify-between">
                        <span className="text-slate-600">{l}</span>
                        <span className="font-mono font-bold text-navy">{formatR(v)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-4 mb-2">By payment method</p>
                  {perf.reconciliation.by_provider.length === 0 && <p className="text-xs text-slate-400">No payments in this period.</p>}
                  {perf.reconciliation.by_provider.map((p) => (
                    <div key={p.provider} className="flex justify-between text-sm" data-testid={`perf-provider-${p.provider}`}>
                      <span className="text-slate-600 capitalize">{p.provider}</span>
                      <span className="font-mono text-navy">{p.orders} · {formatR(p.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="perf-delivery">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Delivery performance</p>
                  <div className="space-y-2 text-sm">
                    {[["Completed deliveries", perf.delivery.completed],
                      ["Pending deliveries", perf.delivery.pending],
                      ["Failed deliveries", perf.delivery.failed]].map(([l, v]) => (
                      <div key={l} className="flex justify-between">
                        <span className="text-slate-600">{l}</span>
                        <span className="font-mono font-bold text-navy">{v}</span>
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <span className="text-slate-600">Avg payment → handover</span>
                      <span className="font-mono font-bold text-navy" data-testid="perf-avg-hours">
                        {perf.delivery.avg_fulfilment_hours === null ? "—" : `${perf.delivery.avg_fulfilment_hours}h`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="perf-missing">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Missing-item claims</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Orders with owed items</span>
                      <span className="font-mono font-bold text-navy">{perf.missing_items.orders_affected}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Units still owed</span>
                      <span className="font-mono font-bold text-navy">{perf.missing_items.units_owed}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-3">Tracked from partial deliveries — see the Outstanding page to clear them.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
